import { useState } from "react";
import { useTranslation } from "react-i18next";
import { baseApi } from "@/shared/api";
import { useAppDispatch } from "@/shared/hooks";
import { extractErrorMessage, downloadBlob } from "@/shared/utils";
import {
  useIssueAdvanceMutation,
  useIssueBusinessTripMutation,
  useDownloadAdvanceT3Mutation,
  useDownloadBusinessTripT3Mutation,
} from "../api";
import type { AdvanceListItem } from "../types";

interface PromptState {
  title: string;
  text: string;
  variant?: "success" | "error" | "warning";
}

export interface UseAdvancesMutationsReturn {
  prompt: PromptState | null;
  setPrompt: (state: PromptState | null) => void;
  issuingId: number | null;
  downloadingId: number | null;
  isIssuing: boolean;
  isDownloadingT3: boolean;
  handleIssue: (entity: Pick<AdvanceListItem, "id" | "type">) => Promise<void>;
  handleDownloadT3: (entity: Pick<AdvanceListItem, "id" | "type">) => Promise<void>;
}

export function useAdvancesMutations(): UseAdvancesMutationsReturn {
  const { t } = useTranslation("AdvancesPage");
  const dispatch = useAppDispatch();

  const [issueAdvance] = useIssueAdvanceMutation();
  const [issueBusinessTrip] = useIssueBusinessTripMutation();
  const [downloadAdvanceT3] = useDownloadAdvanceT3Mutation();
  const [downloadBusinessTripT3] = useDownloadBusinessTripT3Mutation();

  const [prompt, setPrompt] = useState<PromptState | null>(null);
  const [issuingId, setIssuingId] = useState<number | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const isIssuing = issuingId !== null;
  const isDownloadingT3 = downloadingId !== null;

  async function handleIssue(entity: Pick<AdvanceListItem, "id" | "type">) {
    if (isIssuing) return;
    try {
      setIssuingId(entity.id);
      if (entity.type === "trip") {
        await issueBusinessTrip(entity.id).unwrap();
      } else {
        await issueAdvance({ id: entity.id }).unwrap();
      }
      await Promise.all(dispatch(baseApi.util.getRunningQueriesThunk()));
      setPrompt({
        title: t("messages.issueSuccessTitle"),
        text: t("messages.issueSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.issueError", t),
        variant: "error",
      });
    } finally {
      setIssuingId(null);
    }
  }

  async function handleDownloadT3(entity: Pick<AdvanceListItem, "id" | "type">) {
    if (isDownloadingT3) return;
    try {
      setDownloadingId(entity.id);
      let result;
      if (entity.type === "trip") {
        result = await downloadBusinessTripT3(entity.id).unwrap();
      } else {
        result = await downloadAdvanceT3(entity.id).unwrap();
      }
      downloadBlob(result.blob, result.filename || t("messages.t3DefaultFilename"));
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.t3Error", t),
        variant: "error",
      });
    } finally {
      setDownloadingId(null);
    }
  }

  return {
    prompt,
    setPrompt,
    issuingId,
    downloadingId,
    isIssuing,
    isDownloadingT3,
    handleIssue,
    handleDownloadT3,
  };
}
